// Numbers → Day: one trading day. Sales, covers, labour, the nightly report and who worked.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ShoppingBag, CreditCard, Users, Wallet, PieChart, Clock, FileText, CheckCircle2, CircleDashed, AlertTriangle, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { supabase, SUPABASE_URL } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { formatMoney, timeAgo } from '@/src/lib/format';
import { openEodPdf } from '@/src/lib/eodReportFile';
import { Kpi, Section, gridCols, RED, GOLD } from './numbers-ui';

type Money = number | null;
interface DayReport {
  status: 'submitted' | 'draft';
  submittedBy: string | null; submittedAt: string | null; pdfPath: string | null;
  cashTakings: Money; cashVariance: Money; cashNote: string | null; noCash: boolean;
  cards: Money; paymentsVariance: Money; paymentsNote: string | null;
  tips: Money; discounts: Money; deleted: Money; refunds: Money;
  discountLines: Array<{ name: string; amount: number; detail: string }>;
  labour: null | { total: Money; hours: Money; pct: Money; foh: { hours: Money; cost: Money }; boh: { hours: Money; cost: Money } };
  notes: string | null;
}
export interface DayMetrics {
  ok: boolean;
  today: string;
  date: string;
  timezone: string;
  computedAt: string;
  source: 'ordermate' | 'square' | null;
  salesOn: boolean;
  sales: { net: Money; total: Money; covers: number | null; food: Money; bev: Money; perHead: Money };
  lastWeek: { date: string; net: Money; covers: number | null };
  avg4: { net: Money; covers: number | null; weeks: number };
  week: Array<{ day: string; date: string; sales: number }>;
  services: Array<{ name: string; sales: Money; covers: number | null; perHead: Money }>;
  report: DayReport | null;
  labour: null | { connected: true; lastSynced: string | null; hours: number; cost: Money; people: Array<{ name: string; area: string | null; time: string; hours: number; cost: Money }> };
  labourPct: number | null;
  labourCost: Money;
}

function useDayMetrics(venueId: string, date: string | null, refreshKey: number) {
  const [data, setData] = useState<DayMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true); setError(null);
    try {
      const { data: s } = await supabase.auth.getSession();
      if (!s.session) { setError('Not signed in'); setLoading(false); return; }
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/owner-metrics?forceFunctionRegion=ap-southeast-2`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${s.session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: 'day', date }),
      });
      const body = (await resp.json()) as DayMetrics & { error?: string };
      if (id !== reqId.current) return;
      if (!resp.ok || !body.ok || !body.date) setError(body.error ?? (resp.ok ? 'Deploy the latest owner-metrics function to see the day view.' : `Error ${resp.status}`));
      else setData(body);
    } catch (e) {
      if (id === reqId.current) setError(e instanceof Error ? e.message : 'Failed to load');
    }
    if (id === reqId.current) setLoading(false);
  }, [venueId, date]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [load, refreshKey]);
  return { data, loading, error };
}

const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10);
};
const longDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const shortDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const shortDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'UTC' });
const pctChange = (now: Money, then: Money) => (now != null && then != null && then > 0 ? Math.round(((now - then) / then) * 100) : null);
const money = (n: Money) => (n == null ? '—' : formatMoney(n));
const cents = (n: Money) => (n == null ? '—' : `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const goEod = () => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'eod', view: 'numbers' } }));

export default function DayView({ venueId, refreshKey }: { venueId: string; refreshKey: number }) {
  const c = useAppColors();
  const [date, setDate] = useState<string | null>(null); // null = let the server pick (today, or yesterday before any sales)
  const { data, loading, error } = useDayMetrics(venueId, date, refreshKey);
  const shown = date ?? data?.date ?? null;
  const today = data?.today ?? null;
  const pickerRef = useRef<HTMLInputElement>(null);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const busy = loading && (!data || data.date !== shown);
  const d = busy ? null : data;
  const isToday = !!shown && shown === today;
  const relLabel = !shown || !today ? '' : shown === today ? 'Today' : shown === addDays(today, -1) ? 'Yesterday' : '';
  const lwLabel = shown ? `vs last ${shortDay(shown)}` : '';

  const tooltipStyle = { background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 12 };
  const axisTick = { fill: c.textFaint, fontSize: 11 };

  // ---- cards ----
  type K = React.ComponentProps<typeof Kpi>;
  const kpis: K[] = [];
  if (d?.salesOn) {
    const via = d.source === 'square' ? 'Square' : 'OrderMate';
    kpis.push({ label: 'Sales', icon: <ShoppingBag size={15} />, value: money(d.sales.net), sub: `${via}, ex GST`,
      delta: pctChange(d.sales.net, d.lastWeek.net) != null ? { pct: pctChange(d.sales.net, d.lastWeek.net)!, label: lwLabel } : null });
    kpis.push({ label: d.source === 'square' ? 'Orders' : 'Covers', icon: <CreditCard size={15} />, value: d.sales.covers != null ? String(d.sales.covers) : '—',
      sub: d.lastWeek.covers != null ? `${d.lastWeek.covers} last ${shortDay(d.date)}` : undefined,
      delta: pctChange(d.sales.covers, d.lastWeek.covers) != null ? { pct: pctChange(d.sales.covers, d.lastWeek.covers)!, label: lwLabel } : null });
    kpis.push({ label: d.source === 'square' ? 'Avg order' : 'Per head', icon: <Users size={15} />, value: money(d.sales.perHead), sub: 'Ex GST' });
  }
  if (d?.labour || d?.report?.labour) {
    const hours = d.labour ? d.labour.hours : d.report?.labour?.hours ?? null;
    kpis.push({ label: 'Labour cost', icon: <Wallet size={15} />, value: money(d.labourCost),
      sub: d.labour ? (d.labour.cost != null ? 'Deputy roster' : 'Add pay rates in Deputy') : 'From the nightly report' });
    if (d.salesOn) kpis.push({ label: 'Labour %', icon: <PieChart size={15} />, value: d.labourPct != null ? `${d.labourPct}%` : '—', sub: 'Labour ÷ sales ex GST' });
    kpis.push({ label: 'Hours', icon: <Clock size={15} />, value: hours != null ? `${hours}h` : '—', sub: d.labour ? `${d.labour.people.length} shift${d.labour.people.length === 1 ? '' : 's'}` : 'FOH + BOH' });
  }

  const r = d?.report ?? null;
  const showWeek = !!d?.salesOn && d.week.some((w) => w.sales > 0);
  const totalServiceSales = (d?.services ?? []).reduce((a, s) => a + (s.sales ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* Date stepper */}
      <div className="flex items-center gap-2">
        <button onClick={() => shown && setDate(addDays(shown, -1))} disabled={!shown}
          className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center disabled:opacity-40" style={{ background: c.iconBg, color: c.textDim }} aria-label="Previous day">
          <ChevronLeft size={18} />
        </button>
        <button onClick={() => { const el = pickerRef.current; if (!el) return; try { el.showPicker(); } catch { el.focus(); } }}
          className="relative h-10 px-3 sm:px-4 min-w-0 flex-1 sm:flex-none rounded-xl flex items-center justify-center sm:justify-start gap-2 text-sm font-semibold whitespace-nowrap" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
          <Calendar size={14} style={{ color: c.iconColor }} />
          <span className="hidden sm:inline">{shown ? longDate(shown) : 'Loading…'}</span>
          <span className="sm:hidden">{shown ? shortDate(shown) : 'Loading…'}</span>
          {relLabel && <span className="text-xs font-normal" style={{ color: c.textFaint }}>{relLabel}</span>}
          <input ref={pickerRef} type="date" value={shown ?? ''} max={today ?? undefined} tabIndex={-1} aria-hidden
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="absolute inset-0 opacity-0 pointer-events-none" />
        </button>
        <button onClick={() => shown && setDate(addDays(shown, 1))} disabled={!shown || isToday}
          className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center disabled:opacity-40" style={{ background: c.iconBg, color: c.textDim }} aria-label="Next day">
          <ChevronRight size={18} />
        </button>
        {today && !isToday && (
          <button onClick={() => setDate(today)} className="h-10 px-3 rounded-xl text-xs font-semibold shrink-0" style={{ background: c.iconBg, color: c.textDim }}>Today</button>
        )}
      </div>

      {error && <p className="text-sm" style={{ color: RED }}>{error}</p>}

      {/* Nightly report status */}
      {d && (d.source === 'ordermate' || r) && (
        <div className="rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap"
          style={{ background: r?.status === 'submitted' ? 'rgba(74,124,89,0.10)' : c.bgElevated, border: `1px solid ${r?.status === 'submitted' ? c.accentBorder : c.border}` }}>
          {r?.status === 'submitted' ? <CheckCircle2 size={18} style={{ color: c.accentSoft }} /> : <CircleDashed size={18} style={{ color: r ? GOLD : c.textFaint }} />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: c.text }}>
              {r?.status === 'submitted' ? 'Nightly report submitted' : r ? 'Nightly report not finished' : d.salesOn ? 'Sales imported, no nightly report' : 'No nightly report for this day'}
            </p>
            {r?.status === 'submitted' && (
              <p className="text-xs" style={{ color: c.textDim }}>
                {r.submittedBy ? `By ${r.submittedBy}` : 'Submitted'}{r.submittedAt ? ` · ${new Date(r.submittedAt).toLocaleString('en-AU', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: d.timezone })}` : ''}
              </p>
            )}
          </div>
          {r?.status === 'submitted' && r.pdfPath && (
            <button onClick={async () => setPdfErr(await openEodPdf(r.pdfPath!))}
              className="h-9 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5" style={{ background: c.iconBg, color: c.text }}>
              <FileText size={14} /> Open PDF
            </button>
          )}
          {(!r || r.status === 'draft') && (isToday || shown === addDays(today ?? '', -1)) && (
            <button onClick={goEod} className="h-9 px-3 rounded-xl text-xs font-semibold" style={{ background: c.accent, color: '#fff' }}>
              {r ? 'Continue report' : 'Enter numbers'}
            </button>
          )}
          {pdfErr && <p className="w-full text-xs" style={{ color: RED }}>{pdfErr}</p>}
        </div>
      )}

      {/* Cards */}
      {busy ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <Kpi key={i} label="" icon={null} value="" loading />)}
        </div>
      ) : kpis.length > 0 ? (
        <div className={`grid gap-3 ${gridCols(kpis.length)}`}>
          {kpis.map((k) => <Kpi key={k.label} {...k} />)}
        </div>
      ) : d && (
        <section className="rounded-2xl p-8 text-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
          <h2 className="text-base font-semibold" style={{ color: c.text }}>No numbers for {shown ? longDate(shown) : 'this day'}</h2>
          <p className="text-sm mt-1" style={{ color: c.textDim }}>
            {isToday ? 'Today\'s sales show up once the nightly report is in.' : 'Closed, or nothing was recorded for this day.'}
          </p>
        </section>
      )}

      {d && d.salesOn && d.avg4.net != null && d.avg4.weeks >= 2 && (
        <p className="text-xs -mt-3" style={{ color: c.textDim }}>
          Average of the last {d.avg4.weeks} {shortDay(d.date)}s: <b style={{ color: c.text }}>{money(d.avg4.net)}</b>
          {d.avg4.covers ? <> · {d.avg4.covers} covers</> : null}
          {d.sales.net != null && pctChange(d.sales.net, d.avg4.net) != null && (
            <span style={{ color: (pctChange(d.sales.net, d.avg4.net) ?? 0) >= 0 ? c.accentSoft : RED }}>
              {' '}({(pctChange(d.sales.net, d.avg4.net) ?? 0) >= 0 ? '+' : ''}{pctChange(d.sales.net, d.avg4.net)}%)
            </span>
          )}
        </p>
      )}

      {/* Services + the week around it */}
      {d && (d.services.length > 0 || showWeek) && (
        <div className={`grid grid-cols-1 gap-6 ${d.services.length > 0 && showWeek ? 'lg:grid-cols-2' : ''}`}>
          {d.services.length > 0 && (
            <Section title="By service" note="Inc GST, from the printouts">
              <div className="space-y-4">
                {d.services.map((s) => {
                  const share = totalServiceSales > 0 && s.sales != null ? Math.round((s.sales / totalServiceSales) * 100) : null;
                  return (
                    <div key={s.name}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-semibold" style={{ color: c.text }}>{s.name}</span>
                        <span className="text-sm font-semibold tabular-nums" style={{ color: c.text }}>{money(s.sales)}</span>
                      </div>
                      <div className="h-2 rounded-full mt-1.5 overflow-hidden" style={{ background: c.iconBg }}>
                        <div className="h-full rounded-full" style={{ width: `${share ?? 0}%`, background: c.accent }} />
                      </div>
                      <p className="text-xs mt-1" style={{ color: c.textDim }}>
                        {s.covers != null ? `${s.covers} covers` : 'Covers —'}{s.perHead != null ? ` · ${formatMoney(s.perHead)} per head` : ''}{share != null ? ` · ${share}% of the day` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
          {showWeek && (
            <Section title="That week" note="Tap a day · ex GST">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.week} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={c.border} />
                    <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} width={52} tickFormatter={(v: number) => formatMoney(v, { compact: true })} />
                    <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }} formatter={(v) => [formatMoney(Number(v)), 'Sales']} />
                    <Bar dataKey="sales" radius={[6, 6, 0, 0]} className="cursor-pointer"
                      onClick={(_: unknown, i: number) => { const w = d.week[i]; if (w && today && w.date <= today) setDate(w.date); }}>
                      {d.week.map((w) => <Cell key={w.date} fill={w.date === d.date ? c.accent : c.accentBorder} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Nightly report details + who worked */}
      {d && (r?.status === 'submitted' || (d.labour && d.labour.people.length > 0)) && (
        <div className={`grid grid-cols-1 gap-6 ${r?.status === 'submitted' && d.labour && d.labour.people.length > 0 ? 'lg:grid-cols-2' : ''}`}>
          {r?.status === 'submitted' && (
            <Section title="From the nightly report" note={r.submittedBy ?? undefined}>
              <div>
                <Line label="Cash takings" value={r.noCash ? 'No cash taken' : cents(r.cashTakings)} />
                {!r.noCash && <Line label="Cash variance" value={cents(r.cashVariance)} bad={r.cashVariance != null && Math.abs(r.cashVariance) >= 0.01} note={r.cashNote} />}
                <Line label="Cards (ex tips)" value={cents(r.cards)} />
                <Line label="Payments variance" value={cents(r.paymentsVariance)} bad={r.paymentsVariance != null && Math.abs(r.paymentsVariance) >= 0.01} note={r.paymentsNote} />
                {!!r.tips && <Line label="Tips" value={cents(r.tips)} />}
                {!!r.discounts && <Line label="Discounts and comps" value={cents(r.discounts)}
                  note={r.discountLines.map((x) => `${x.name} ${cents(x.amount)}${x.detail ? `: ${x.detail}` : ''}`).join(' · ') || null} />}
                {!!r.deleted && <Line label="Deleted items" value={cents(r.deleted)} />}
                {!!r.refunds && <Line label="Refunds" value={cents(r.refunds)} />}
                {r.labour && (r.labour.foh.cost != null || r.labour.boh.cost != null) && (
                  <Line label="Labour FOH / BOH" value={`${money(r.labour.foh.cost)} / ${money(r.labour.boh.cost)}`}
                    note={`${r.labour.foh.hours ?? 0}h front · ${r.labour.boh.hours ?? 0}h kitchen`} />
                )}
              </div>
              {r.notes && (
                <div className="mt-4 rounded-xl p-3 text-sm whitespace-pre-wrap" style={{ background: c.iconBg, color: c.text }}>
                  <span className="block text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: c.textFaint }}>Manager's notes</span>
                  {r.notes}
                </div>
              )}
            </Section>
          )}
          {d.labour && d.labour.people.length > 0 && (
            <Section title="Who worked" note={d.labour.lastSynced ? `Deputy · synced ${timeAgo(d.labour.lastSynced)}` : 'Deputy'}>
              <div>
                {d.labour.people.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5 border-t first:border-t-0" style={{ borderColor: c.border }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: c.text }}>{p.name}</p>
                      <p className="text-xs truncate" style={{ color: c.textDim }}>{p.time}{p.area ? ` · ${p.area}` : ''}</p>
                    </div>
                    <span className="text-xs tabular-nums" style={{ color: c.textDim }}>{p.hours}h</span>
                    <span className="text-sm tabular-nums w-16 text-right" style={{ color: c.text }}>{p.cost != null ? formatMoney(p.cost) : '—'}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

    </div>
  );
}

function Line({ label, value, bad, note }: { label: string; value: string; bad?: boolean; note?: string | null }) {
  const c = useAppColors();
  return (
    <div className="py-2.5 border-t first:border-t-0" style={{ borderColor: c.border }}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm flex items-center gap-1.5" style={{ color: c.textDim }}>
          {bad && <AlertTriangle size={13} style={{ color: RED }} />}{label}
        </span>
        <span className="text-sm font-semibold tabular-nums" style={{ color: bad ? RED : c.text }}>{value}</span>
      </div>
      {note && <p className="text-xs mt-0.5" style={{ color: c.textFaint }}>{note}</p>}
    </div>
  );
}
